import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  MusicConfig,
  OverlayLayer,
  ReelScene,
  SceneMeta,
  SfxEvent,
  SfxName,
  VoiceoverConfig,
  VoiceoverSegment,
  WatermarkConfig,
} from "../../api";
import { listEmbeddedSfxChips, type EmbeddedSfxChipDesc } from "@engine/sfx/embeddedTimeline";
import { Tooltip } from "./Tooltip";
import { getSfxLibEntry, SFX_DRAG_MIME } from "./SfxGallery";
import { WaveformCanvas } from "./WaveformCanvas";

/** Friendly chip glyph + label per overlay kind. Shared with the preview's
 *  selection chip so the user sees the same icon in both places. */
function overlayChipBadge(kind: OverlayLayer["kind"]): { glyph: string; label: string; tone: string } {
  switch (kind) {
    case "text":
      return { glyph: "T", label: "Text", tone: "text" };
    case "shape":
      return { glyph: "▭", label: "Shape", tone: "shape" };
    case "icon":
      return { glyph: "✦", label: "Icon", tone: "icon" };
    case "badge":
      return { glyph: "●", label: "Badge", tone: "badge" };
    case "image":
      return { glyph: "🖼", label: "Bild", tone: "image" };
    case "group":
      return { glyph: "📁", label: "Gruppe", tone: "group" };
  }
}

/** Short, never-empty body text for the chip label. */
function overlayChipText(o: OverlayLayer): string {
  switch (o.kind) {
    case "text":
      return (o.text || "Text").split("\n")[0];
    case "shape":
      return o.shape;
    case "icon":
      return o.icon;
    case "badge":
      return o.text || "Badge";
    case "image":
      return "Bild";
    case "group":
      return o.name || "Gruppe";
  }
}

/**
 * Resolved per-clip layout produced by `resolveClipLayout` (templates.ts).
 * The Composer is the single source of truth — Timeline just renders.
 */
export type ClipLayout = {
  scene: ReelScene;
  fromFrames: number;
  durFrames: number;
  track: number;
};

type Props = {
  scenes: ReelScene[];
  /** Pre-resolved layout (from / duration / track per clip), in frames. */
  layout: ClipLayout[];
  sceneIndex: Record<string, SceneMeta>;
  selectedUid: string | null;
  totalSec: number;
  targetSec: number;
  fps: number;
  /** Highest used track index — used to know how many lanes to render (+1 empty). */
  maxTrack: number;
  music?: MusicConfig;
  /** Patch music timeline fields (move/resize/fade). When omitted, the
   *  music bar is read-only on the timeline. */
  onPatchMusic?: (patch: Partial<MusicConfig>) => void;
  /** Voiceover-Spur (eine pro Reel, beliebig viele Schnitt-Segmente). */
  voiceover?: VoiceoverConfig;
  /** Patch der Voiceover-Spur als Ganzes (Volume/Label/Enabled, oder das
   *  ERSTE Segment via Legacy-Felder). */
  onPatchVoiceover?: (patch: Partial<VoiceoverConfig>) => void;
  /** Patch eines einzelnen VO-Segments (move/resize/trim/fade auf der Bar). */
  onPatchVoiceoverSegment?: (
    segmentId: string,
    patch: Partial<{
      fromSec: number;
      durSec: number;
      trimStartSec: number;
      fadeInSec: number;
      fadeOutSec: number;
    }>,
  ) => void;
  /** Entfernt EIN Segment (Backspace auf selektiertem Bar oder Ctx-Menu). */
  onRemoveVoiceoverSegment?: (segmentId: string) => void;
  /** Schneidet die VO-Spur am Playhead in zwei Stücke (Blade-Tool). */
  onSplitVoiceoverAtPlayhead?: () => void;
  /** Master-Mute der gesamten SFX-Zeile (Vorlagen-SFX + unabhängige Events).
   *  Wenn aktiv, wird die SFX-Spur visuell als gemutet markiert und in
   *  der Vorschau via Composer komplett unterdrückt. */
  sfxLineMuted?: boolean;
  /** Toggle für den Master-SFX-Mute (kleines Speaker-Icon im SFX-Gutter). */
  onToggleSfxLineMute?: () => void;
  watermark?: WatermarkConfig;
  playheadSec?: number;
  isPlaying?: boolean;
  /** Snap to other clips' edges + playhead + 1s grid. */
  snap?: boolean;
  onSelect: (uid: string) => void;
  onRemove: (uid: string) => void;
  onDuplicate: (uid: string) => void;
  /** Split the clip at the current playhead. */
  onSplit?: (uid: string) => void;
  /** Patches one clip's timeline position / size / track. */
  onPatchTime: (uid: string, patch: { fromSec?: number; durationSec?: number; naturalDurationSec?: number; track?: number }) => void;
  /** Swap a scene's sequence position with its temporal neighbour. Direction
   *  "up" pulls the clip in front of the previous one (earlier in time);
   *  "down" pushes it past the next one. The Composer takes care of
   *  recomputing fromSec values so the timeline stays gapless. */
  onMoveSceneSequence?: (uid: string, direction: "up" | "down") => void;
  onSeek?: (sec: number) => void;
  onFocusTrack?: (track: "music" | "voiceover" | "watermark" | "sfx") => void;

  /* ---- Overlay-track (text/shape/icon/badge/image) ---- */
  overlays?: OverlayLayer[];
  /** Patch an overlay's time/duration/track directly from the timeline. */
  onPatchOverlayTime?: (uid: string, patch: { fromSec?: number; durSec?: number; track?: number }) => void;
  onRemoveOverlay?: (uid: string) => void;
  onDuplicateOverlay?: (uid: string) => void;
  /** Library-drop onto the overlays track — receives the element id from the
   *  drag payload plus the target fromSec and track (dense-lane mapped).
   *  When omitted, the overlays track ignores library drags. */
  onDropElementOnTimeline?: (
    elementId: string,
    opts: { fromSec: number; track: number },
  ) => void;

  /* ---- Independent SFX events (own bars on the SFX lane) ---- */
  sfxEvents?: SfxEvent[];
  /** Preview format — drives embedded scene-SFX chip positions. */
  format?: "9:16" | "1:1" | "16:9";
  /** Scene-baked SFX: mute one beat (extras.embeddedSfxSuppress indices). */
  onSuppressEmbeddedSfxBeat?: (sceneUid: string, embedIndex: number) => void;
  /** Scene-baked SFX without recipe: mute whole scene extras.sfx. */
  onMuteEmbeddedSceneBundledSfx?: (sceneUid: string) => void;
  onPatchSfxEventTime?: (uid: string, patch: { fromSec?: number; durSec?: number }) => void;
  onRemoveSfxEvent?: (uid: string) => void;
  onDuplicateSfxEvent?: (uid: string) => void;
  /** Drop from the SFX gallery onto the SFX lane. */
  onDropSfxOnTimeline?: (
    sound: SfxName,
    opts: {
      fromSec: number;
      durSec: number;
      custom?: { src: string; assetId?: string; label?: string };
    },
  ) => void;

  /** Horizontaler Zoom (1.0 = passt komplett in die Breite, >1 = zoomt rein,
   *  <1 = rauszoomen). Wird vom Composer kontrolliert + persistiert. */
  zoom?: number;
  onZoomChange?: (next: number) => void;
  /** Höhen-Multiplikator für die Audio-Sub-Tracks (Music/VO/SFX). 1 = default.
   *  Wir geben 0.6–2.5 frei, damit der User je nach Bedarf zwischen "alles
   *  kompakt" und "ich will die Wellenform sehen" wechseln kann. */
  audioRowScale?: number;
  onAudioRowScaleChange?: (next: number) => void;
};

/** Minimum clip width in seconds. */
const MIN_DUR_SEC = 0.4;
/** Lane height in px. */
const LANE_H = 56;
/** Lane height for sub-tracks (music/logo/sfx/overlays). */
const SUB_LANE_H = 36;
/** Snap distance in pixels — anything within this of a snap target gets pulled. */
const SNAP_PX = 8;

// =============================================================================
// SNAP HELPERS
// =============================================================================

type SnapTarget = { sec: number; kind: "playhead" | "clip-start" | "clip-end" | "grid" | "zero" | "target" };

/** Build a flat list of snap target seconds from clips + playhead + ruler. */
function buildSnapTargets(
  layout: ClipLayout[],
  fps: number,
  playheadSec: number,
  totalForBar: number,
  targetSec: number,
  excludeUid: string | null,
): SnapTarget[] {
  const out: SnapTarget[] = [
    { sec: 0, kind: "zero" },
    { sec: playheadSec, kind: "playhead" },
    { sec: targetSec, kind: "target" },
  ];
  // Per-second grid lines (whole seconds across the visible range).
  for (let i = 0; i <= Math.ceil(totalForBar); i += 1) out.push({ sec: i, kind: "grid" });
  // Other clip starts/ends (excluding the one being dragged).
  for (const c of layout) {
    if (c.scene.uid === excludeUid) continue;
    out.push({ sec: c.fromFrames / fps, kind: "clip-start" });
    out.push({ sec: (c.fromFrames + c.durFrames) / fps, kind: "clip-end" });
  }
  return out;
}

/**
 * Find the best snap target for a candidate value. Returns the snapped value
 * AND the target kind so we can render a guide line + tooltip badge.
 */
function applySnap(
  candidateSec: number,
  targets: SnapTarget[],
  pxPerSec: number,
): { sec: number; hit: SnapTarget | null } {
  if (pxPerSec <= 0) return { sec: candidateSec, hit: null };
  const tolSec = SNAP_PX / pxPerSec;
  let best: SnapTarget | null = null;
  let bestDist = tolSec;
  for (const t of targets) {
    const d = Math.abs(t.sec - candidateSec);
    if (d <= bestDist) {
      bestDist = d;
      best = t;
    }
  }
  return best ? { sec: best.sec, hit: best } : { sec: candidateSec, hit: null };
}

// =============================================================================
// TIMELINE
// =============================================================================

export const Timeline: React.FC<Props> = ({
  scenes,
  layout,
  sceneIndex,
  selectedUid,
  totalSec,
  targetSec,
  fps,
  maxTrack,
  music,
  onPatchMusic,
  voiceover,
  onPatchVoiceover,
  onPatchVoiceoverSegment,
  onRemoveVoiceoverSegment,
  onSplitVoiceoverAtPlayhead,
  sfxLineMuted = false,
  onToggleSfxLineMute,
  watermark,
  playheadSec = 0,
  isPlaying = false,
  snap = false,
  onSelect,
  onRemove,
  onDuplicate,
  onSplit,
  onPatchTime,
  onMoveSceneSequence,
  onSeek,
  onFocusTrack,
  overlays = [],
  onPatchOverlayTime,
  onRemoveOverlay,
  onDuplicateOverlay,
  onDropElementOnTimeline,
  sfxEvents = [],
  format = "9:16",
  onSuppressEmbeddedSfxBeat,
  onMuteEmbeddedSceneBundledSfx,
  onPatchSfxEventTime,
  onRemoveSfxEvent,
  onDuplicateSfxEvent,
  onDropSfxOnTimeline,
  zoom = 1,
  onZoomChange,
  audioRowScale = 1,
  onAudioRowScaleChange,
}) => {
  const ELEMENT_DRAG_MIME = "application/x-jusa-element";
  const totalForBar = Math.max(totalSec, targetSec, 0.001);
  const playheadPct = Math.max(0, Math.min(100, (playheadSec / totalForBar) * 100));
  // Effektive Zoom-Werte clampen — verhindert „Bar verschwindet", wenn der
  // User den Slider extrem zieht oder Strg+Wheel zu schnell triggert.
  const effZoom = Math.max(0.4, Math.min(12, zoom));
  const effAudioScale = Math.max(0.5, Math.min(3, audioRowScale));
  // Sub-track height für Audio-Lanes (Music/VO/SFX) — der Scenes-Bereich + die
  // Overlays bleiben unbenetzt, weil die User dort üblicherweise weniger
  // Höhe brauchen. Watermark-Spur skaliert mit, damit das Logo-Segment-
  // Strip nicht plötzlich winzig wirkt zwischen riesigen Audio-Bars.
  const audioLaneH = Math.round(SUB_LANE_H * effAudioScale);

  const trackAreaRef = useRef<HTMLDivElement | null>(null);
  const rulerRef = useRef<HTMLDivElement | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);

  // ----- Live drag overlay state (shown above clips) -----
  type DragHud = {
    snapSec: number | null;
    snapKind: SnapTarget["kind"] | null;
    targetTrack: number | null;
    tooltip: { x: number; y: number; text: string } | null;
  } | null;
  const [dragHud, setDragHud] = useState<DragHud>(null);

  // ----- Right-click context menu -----
  const [ctxMenu, setCtxMenu] = useState<
    | null
    | { uid: string; x: number; y: number }
  >(null);
  useEffect(() => {
    if (!ctxMenu) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest(".timeline-ctx-menu")) return;
      setCtxMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCtxMenu(null);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [ctxMenu]);

  const seekFromClientX = (clientX: number): void => {
    const el = rulerRef.current;
    if (!el || !onSeek) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    onSeek((x / rect.width) * totalForBar);
  };

  useEffect(() => {
    if (!isScrubbing) return;
    const onMove = (e: MouseEvent) => seekFromClientX(e.clientX);
    const onUp = () => setIsScrubbing(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrubbing, totalForBar]);

  // ----- Watermark + SFX overlay segments (driven by the resolved layout) -----
  const watermarkSegments = useMemo(() => {
    if (!watermark?.enabled) return [];
    return layout
      .map((c) => {
        const hideLogo = (c.scene.inputs.extras?.hideLogo as boolean | undefined) === true;
        if (hideLogo) return null;
        const startSec = c.fromFrames / fps;
        const widthSec = c.durFrames / fps;
        return {
          uid: c.scene.uid,
          leftPct: (startSec / totalForBar) * 100,
          widthPct: (widthSec / totalForBar) * 100,
        };
      })
      .filter((x): x is { uid: string; leftPct: number; widthPct: number } => x !== null);
  }, [watermark?.enabled, layout, totalForBar, fps]);

  const embeddedSfxChips: EmbeddedSfxChipDesc[] = useMemo(
    () => listEmbeddedSfxChips(layout, fps, format),
    [layout, fps, format],
  );

  // ----- SFX-Bar drag state (shows extra row affordance during drag like overlays) -----
  const [isSfxDragging, setIsSfxDragging] = useState(false);
  const [isSfxLibDragOver, setIsSfxLibDragOver] = useState(false);

  // ----- Overlay drag state (so Timeline knows when to reveal an extra lane) -----
  const [isOverlayDragging, setIsOverlayDragging] = useState(false);
  /** A library element is being dragged over the overlay track. Triggers the
   *  same "extra empty lane" reveal so the user can drop into a fresh row. */
  const [isElementDragOver, setIsElementDragOver] = useState<{ laneIdx: number } | null>(null);
  const isAnyDragging = dragHud !== null || isOverlayDragging || isElementDragOver !== null;

  // ----- Scene lanes: only show an extra drop lane WHILE dragging (CapCut-style) -----
  const baseLaneCount = Math.max(1, maxTrack + 1);
  const laneCount = baseLaneCount + (isAnyDragging ? 1 : 0);
  const lanes = Array.from({ length: laneCount }, (_, i) => i);
  const sceneAreaHeight = laneCount * LANE_H + 4;

  // ----- Overlay lanes: DENSE display mapping -----
  // Overlay `track` can be anything (templates often use 10+). We map the
  // unique, sorted real-track values to dense "display lanes" (0, 1, 2, …)
  // so the Timeline never shows empty gaps. Vertical drag works in the dense
  // lane space and patches the real track accordingly.
  const { uniqueOverlayTracks, trackToLane } = useMemo(() => {
    const visible = overlays.filter((o) => o.kind !== "group");
    const uniq = Array.from(new Set(visible.map((o) => o.track))).sort(
      (a, b) => a - b,
    );
    const map = new Map<number, number>();
    uniq.forEach((t, i) => map.set(t, i));
    return { uniqueOverlayTracks: uniq, trackToLane: map };
  }, [overlays]);
  const baseOverlayLaneCount = Math.max(1, uniqueOverlayTracks.length);
  const overlayLaneCount = baseOverlayLaneCount + (isAnyDragging ? 1 : 0);
  const overlayLanes = Array.from({ length: overlayLaneCount }, (_, i) => i);
  const overlayAreaHeight = overlayLaneCount * SUB_LANE_H + 4;
  const singleSubHeight = SUB_LANE_H + 4;
  /** Höhe einer Audio-Sub-Lane (Music/VO/SFX). Skaliert via `audioRowScale`. */
  const audioSubHeight = audioLaneH + 4;
  /** Innere Bar-Höhe für Audio-Spuren (für absolute-positioned Bars im Track). */
  const audioBarH = audioLaneH - 8;

  /** Convert a dense display-lane index to a real overlay `track` value. */
  const laneToTrack = useCallback(
    (laneIdx: number): number => {
      if (laneIdx < uniqueOverlayTracks.length) return uniqueOverlayTracks[laneIdx];
      // Lane beyond the last existing real track → create a new track one above.
      const top = uniqueOverlayTracks.length > 0
        ? uniqueOverlayTracks[uniqueOverlayTracks.length - 1]
        : -1;
      return top + 1 + (laneIdx - uniqueOverlayTracks.length);
    },
    [uniqueOverlayTracks],
  );

  /** Convert a clientX inside the track area to seconds. */
  const xToSec = useCallback((clientX: number): number => {
    const el = trackAreaRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    return (x / rect.width) * totalForBar;
  }, [totalForBar]);

  /** Pixels per second within the track area — used by snap math + tooltip. */
  const pxPerSec = useCallback((): number => {
    const el = trackAreaRef.current;
    if (!el) return 0;
    return el.getBoundingClientRect().width / Math.max(0.001, totalForBar);
  }, [totalForBar]);

  // ----- Background click on empty timeline → deselect -----
  const onTrackBgMouseDown = (e: React.MouseEvent): void => {
    if (e.target !== trackAreaRef.current) return;
    onSelect("");
  };

  const ctxMenuScene = ctxMenu ? scenes.find((s) => s.uid === ctxMenu.uid) ?? null : null;

  // ----- Strg/Cmd + Wheel = horizontaler Zoom -----
  // Wir registrieren den Listener als non-passive (bzw. via React onWheel
  // mit preventDefault), weil sonst der Browser die Seite scrollt statt
  // zu zoomen. Faktor 1.12 pro Tick fühlt sich nicht-jumpig an.
  const onTracksWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!onZoomChange) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const next = Math.max(0.4, Math.min(12, effZoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12)));
      onZoomChange(Math.round(next * 100) / 100);
    },
    [effZoom, onZoomChange],
  );

  // ============== Render ==============

  return (
    <div className="timeline">
      {/* ===== Timeline-Toolbar — Zoom, Lane-Höhe, Blade, Delete ===== */}
      <div className="timeline-toolbar">
        <div className="tl-tool-group">
          <button
            type="button"
            className="tl-tool-btn"
            disabled={!onZoomChange}
            onClick={() => onZoomChange?.(Math.max(0.4, Math.round(effZoom / 1.25 * 100) / 100))}
            title={"Auszoomen (Strg/Cmd + Mausrad oder Minus-Taste)"}
          >
            −
          </button>
          <input
            type="range"
            className="tl-zoom-slider"
            min={0.4}
            max={12}
            step={0.1}
            value={effZoom}
            onChange={(e) => onZoomChange?.(Number(e.target.value))}
            title="Zoom: zieht die Timeline horizontal weiter — perfekt zum frame-genauen Schneiden"
          />
          <button
            type="button"
            className="tl-tool-btn"
            disabled={!onZoomChange}
            onClick={() => onZoomChange?.(Math.min(12, Math.round(effZoom * 1.25 * 100) / 100))}
            title={"Reinzoomen (Strg/Cmd + Mausrad oder Plus-Taste)"}
          >
            +
          </button>
          <span className="tl-zoom-val">{Math.round(effZoom * 100)}%</span>
          <button
            type="button"
            className="tl-tool-btn ghost"
            disabled={!onZoomChange}
            onClick={() => onZoomChange?.(1)}
            title="Zoom auf 100% zurücksetzen"
          >
            ⤺
          </button>
        </div>

        <div className="tl-tool-group">
          <span className="tl-tool-label">Audio-Höhe</span>
          <input
            type="range"
            className="tl-zoom-slider"
            min={0.5}
            max={3}
            step={0.1}
            value={effAudioScale}
            onChange={(e) => onAudioRowScaleChange?.(Number(e.target.value))}
            title="Höhe der Audio-Spuren (Music/VO/SFX) — höher = mehr Wellenform-Detail"
          />
          <span className="tl-zoom-val">{Math.round(effAudioScale * 100)}%</span>
        </div>

        <div className="tl-tool-group tl-tool-group-actions">
          {onSplitVoiceoverAtPlayhead && (
            <button
              type="button"
              className="tl-tool-btn primary"
              onClick={onSplitVoiceoverAtPlayhead}
              disabled={!voiceover?.enabled || !voiceover.src}
              title="Voiceover am Playhead in zwei Stücke schneiden (Tastenkürzel: S)"
            >
              ✂ Schneiden (S)
            </button>
          )}
        </div>
      </div>

      <div className="timeline-grid">
        {/* ---------- LEFT GUTTER (track labels) ---------- */}
        <div className="timeline-gutter">
          <div className="track-label-cell ruler-label">
            <span className="track-label-icon">⏱</span>
          </div>
          <Tooltip
            title="Layer-Spuren"
            description="L0 ist die Basis-Schicht, höhere Layer (L1, L2, …) liegen DARÜBER. Clips oben überdecken Clips unten — perfekt für Overlays wie Logos, Sticker oder Text."
          >
            <div
              className="track-label-cell scenes-label"
              style={{ height: sceneAreaHeight }}
              data-tip-host="true"
            >
              <div className="scenes-label-head">
                <span className="track-label-icon">🎬</span>
                <span className="track-label-text">Layers</span>
              </div>
              <div className="scenes-lane-labels">
                {lanes
                  .slice()
                  .reverse()
                  .map((laneIdx) => (
                    <div key={laneIdx} className="scenes-lane-label" style={{ height: LANE_H }}>
                      <span className={`lane-pill ${laneIdx === 0 ? "base" : ""}`}>L{laneIdx}</span>
                    </div>
                  ))}
              </div>
            </div>
          </Tooltip>
          <Tooltip
            title="Music-Spur"
            description="Hintergrundmusik fürs gesamte Reel — Lautstärke, Fade-In/Out und Track-Auswahl im rechten Panel. Speaker-Icon = ganze Music-Zeile stumm."
          >
            <div
              className={`track-label-cell sub-label sub-multi-label clickable ${music?.enabled ? "active" : ""} ${music && !music.enabled ? "is-muted" : ""}`}
              style={{ height: audioSubHeight }}
              onClick={() => onFocusTrack?.("music")}
              data-tip-host="true"
            >
              <span className="track-label-icon">🎵</span>
              <span className="track-label-text">Music</span>
              <button
                type="button"
                className={`track-mute-btn ${music && !music.enabled ? "is-muted" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!onPatchMusic) return;
                  // Wenn noch keine Musik existiert: nichts tun (User muss
                  // zuerst ein Bed/Asset wählen). Sonst toggle enabled.
                  if (!music) return;
                  onPatchMusic({ enabled: !music.enabled });
                }}
                title={
                  !music
                    ? "Noch keine Musik gewählt — im Music-Panel ein Bed oder Asset auswählen"
                    : music.enabled
                      ? "Music-Zeile stumm schalten"
                      : "Music-Zeile aktivieren"
                }
                aria-label="Music-Zeile stumm/an"
                aria-pressed={!!music && !music.enabled}
                disabled={!music}
              >
                {music && !music.enabled ? "🔇" : "🔊"}
              </button>
              <span className="lane-pill sub-pill">M0</span>
            </div>
          </Tooltip>
          <Tooltip
            title="Voiceover-Spur"
            description="Eigene Sprachaufnahme als eigene Audiospur — hochladen, ziehen zum Verschieben, Ränder zum Trimmen, Lautstärke + Fades im rechten Panel. Speaker-Icon = Spur stumm."
          >
            <div
              className={`track-label-cell sub-label sub-multi-label clickable ${voiceover?.enabled && voiceover.src ? "active" : ""} ${voiceover && !voiceover.enabled ? "is-muted" : ""}`}
              style={{ height: audioSubHeight }}
              onClick={() => onFocusTrack?.("voiceover")}
              data-tip-host="true"
            >
              <span className="track-label-icon">🎙️</span>
              <span className="track-label-text">Voiceover</span>
              <button
                type="button"
                className={`track-mute-btn ${voiceover && !voiceover.enabled ? "is-muted" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!onPatchVoiceover) return;
                  if (!voiceover) return;
                  onPatchVoiceover({ enabled: !voiceover.enabled });
                }}
                title={
                  !voiceover?.src
                    ? "Noch keine Voiceover-Datei — im Voiceover-Panel hochladen"
                    : voiceover.enabled
                      ? "Voiceover stumm schalten"
                      : "Voiceover aktivieren"
                }
                aria-label="Voiceover stumm/an"
                aria-pressed={!!voiceover && !voiceover.enabled}
                disabled={!voiceover?.src}
              >
                {voiceover && !voiceover.enabled ? "🔇" : "🔊"}
              </button>
              <span className="lane-pill sub-pill">V0</span>
            </div>
          </Tooltip>
          <Tooltip
            title="Logo / Watermark"
            description="Globales Logo, das über allen Scenes liegt. Im Vorschau-Bild ziehbar oder im Logo-Panel via Position/Größe einstellbar."
          >
            <div
              className={`track-label-cell sub-label sub-multi-label clickable ${watermark?.enabled ? "active" : ""}`}
              style={{ height: singleSubHeight }}
              onClick={() => onFocusTrack?.("watermark")}
              data-tip-host="true"
            >
              <span className="track-label-icon">🎯</span>
              <span className="track-label-text">Logo</span>
              <span className="lane-pill sub-pill">W0</span>
            </div>
          </Tooltip>
          <Tooltip
            title="Sound-Effekte"
            description="Eigene SFX-Balken (Library) plus eingebettete Vorlagen-SFX je Szene — sichtbar, einzeln löschbar (Szene-SFX). Speaker-Icon = ganze SFX-Zeile stumm."
          >
            <div
              className={`track-label-cell sub-label sub-multi-label clickable ${
              sfxEvents.length > 0 || embeddedSfxChips.length > 0 ? "active" : ""
            } ${sfxLineMuted ? "is-muted" : ""}`}
              style={{ height: audioSubHeight }}
              onClick={() => onFocusTrack?.("sfx")}
              data-tip-host="true"
            >
              <span className="track-label-icon">🔊</span>
              <span className="track-label-text">SFX</span>
              <button
                type="button"
                className={`track-mute-btn ${sfxLineMuted ? "is-muted" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSfxLineMute?.();
                }}
                title={sfxLineMuted ? "SFX-Zeile aktivieren" : "Komplette SFX-Zeile stumm schalten"}
                aria-label="SFX-Zeile stumm/an"
                aria-pressed={sfxLineMuted}
              >
                {sfxLineMuted ? "🔇" : "🔊"}
              </button>
              <span className="lane-pill sub-pill">S0</span>
            </div>
          </Tooltip>
          <Tooltip
            title="Overlay-Spur"
            description="Text, Shapes, Icons, Badges und Bilder als eigene Clips. Klick = auswählen, ziehen = verschieben, Ränder = Dauer ändern. Drag&Drop aus der Element-Library."
          >
            <div
              className={`track-label-cell scenes-label sub-multi-label ${overlays.length ? "active" : ""}`}
              style={{ height: overlayAreaHeight }}
              data-tip-host="true"
            >
              <div className="scenes-label-head">
                <span className="track-label-icon">✦</span>
                <span className="track-label-text">Overlays</span>
              </div>
              <div className="scenes-lane-labels">
                {overlayLanes
                  .slice()
                  .reverse()
                  .map((laneIdx) => (
                    <div key={laneIdx} className="scenes-lane-label" style={{ height: SUB_LANE_H }}>
                      <span className={`lane-pill ${laneIdx === 0 ? "base" : ""}`}>O{laneIdx}</span>
                    </div>
                  ))}
              </div>
            </div>
          </Tooltip>
        </div>

        {/* ---------- RIGHT TRACKS COLUMN ---------- */}
        <div className="timeline-tracks" onWheel={onTracksWheel}>
         <div
           className="timeline-tracks-inner"
           style={{ width: `${effZoom * 100}%` }}
         >
          {/* Continuous playhead spanning all tracks */}
          <div
            className={`timeline-playhead-vert ${isPlaying ? "playing" : ""}`}
            style={{ left: `${playheadPct}%` }}
            aria-hidden
          />

          {/* ----- Ruler ----- */}
          <div
            className="timeline-ruler"
            ref={rulerRef}
            onMouseDown={(e) => {
              if (!onSeek) return;
              setIsScrubbing(true);
              seekFromClientX(e.clientX);
            }}
            style={{ cursor: onSeek ? "ew-resize" : "default" }}
          >
            {Array.from({ length: Math.ceil(totalForBar) + 1 }).map((_, i) => (
              <div
                key={i}
                className="tick"
                style={{ left: `${(i / totalForBar) * 100}%` }}
              >
                <span>{i}s</span>
              </div>
            ))}
            <div
              className="target-marker"
              style={{ left: `${(targetSec / totalForBar) * 100}%` }}
              title={`Ziel: ${targetSec}s`}
            >
              <span>Ziel {targetSec}s</span>
            </div>
            <div
              className={`timeline-playhead ${isPlaying ? "playing" : ""} ${isScrubbing ? "dragging" : ""}`}
              style={{ left: `${playheadPct}%` }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setIsScrubbing(true);
              }}
              title={`${playheadSec.toFixed(2)}s · zum Scrubben ziehen`}
            >
              <div className="playhead-handle">
                <span className="playhead-time">{playheadSec.toFixed(1)}s</span>
              </div>
            </div>
          </div>

          {/* ----- Multi-lane scene track ----- */}
          <div
            className="track-row scene-track-row multi-lane"
            ref={trackAreaRef}
            onMouseDown={onTrackBgMouseDown}
            onContextMenu={(e) => {
              // Allow ctx-menu only when right-clicking a clip; on bg, suppress.
              const t = e.target as HTMLElement;
              if (!t.closest(".freeform-clip")) e.preventDefault();
            }}
            style={{ height: sceneAreaHeight, position: "relative" }}
          >
            {/* Empty lane backgrounds — anchored to BOTTOM so L0 never shifts
             *  even if the container grows during a drag. */}
            {lanes.map((laneIdx) => (
              <div
                key={laneIdx}
                className={`scene-lane ${laneIdx === 0 ? "base-lane" : "overlay-lane"} ${
                  dragHud?.targetTrack === laneIdx ? "drop-target" : ""
                }`}
                style={{ height: LANE_H, bottom: laneIdx * LANE_H }}
              >
                <div className={`lane-tag ${snap ? "snap-on" : ""}`}>L{laneIdx}</div>
              </div>
            ))}

            {/* Snap grid lines (every 1s) — only when snap is on */}
            {snap &&
              Array.from({ length: Math.ceil(totalForBar) + 1 }).map((_, i) => (
                <div
                  key={`grid-${i}`}
                  className="snap-grid-line"
                  style={{ left: `${(i / totalForBar) * 100}%`, height: sceneAreaHeight }}
                />
              ))}

            {/* Live snap indicator while dragging */}
            {dragHud?.snapSec != null && (
              <div
                className={`snap-indicator kind-${dragHud.snapKind ?? "grid"}`}
                style={{
                  left: `${(dragHud.snapSec / totalForBar) * 100}%`,
                  height: sceneAreaHeight,
                }}
                aria-hidden
              />
            )}

            {scenes.length === 0 && (
              <div className="empty multi-lane-empty">
                Scenes aus der Library links rüberziehen oder anklicken.
              </div>
            )}

            {/* Clips, absolutely positioned by from / duration / track. */}
            {layout.map((c) => {
              const meta = sceneIndex[c.scene.sceneId];
              const startSec = c.fromFrames / fps;
              const widthSec = c.durFrames / fps;
              const leftPct = (startSec / totalForBar) * 100;
              const widthPct = Math.max(
                (MIN_DUR_SEC / totalForBar) * 100,
                (widthSec / totalForBar) * 100,
              );
              const bottom = c.track * LANE_H + 4;
              return (
                <FreeformClip
                  key={c.scene.uid}
                  scene={c.scene}
                  resolvedFromSec={startSec}
                  resolvedDurSec={widthSec}
                  resolvedTrack={c.track}
                  label={meta?.label ?? c.scene.sceneId}
                  look={meta?.look ?? "minimal"}
                  leftPct={leftPct}
                  widthPct={widthPct}
                  bottomPx={bottom}
                  laneHeight={LANE_H - 8}
                  selected={selectedUid === c.scene.uid}
                  trackAreaRef={trackAreaRef}
                  totalForBar={totalForBar}
                  maxTrack={laneCount - 1}
                  fps={fps}
                  layout={layout}
                  playheadSec={playheadSec}
                  targetSec={targetSec}
                  snap={snap}
                  xToSec={xToSec}
                  pxPerSec={pxPerSec}
                  onSelect={() => onSelect(c.scene.uid)}
                  onRemove={() => onRemove(c.scene.uid)}
                  onDuplicate={() => onDuplicate(c.scene.uid)}
                  onPatch={(patch) => onPatchTime(c.scene.uid, patch)}
                  onContextMenu={(x, y) => {
                    onSelect(c.scene.uid);
                    setCtxMenu({ uid: c.scene.uid, x, y });
                  }}
                  onDragHud={setDragHud}
                />
              );
            })}
          </div>

          {/* ----- Music multi-lane ----- */}
          <div
            className="track-row scene-track-row multi-lane sub-multi-track"
            style={{ height: audioSubHeight, position: "relative" }}
          >
            <div className="scene-lane base-lane" style={{ height: audioLaneH, top: 0 }}>
              <div className="lane-tag">M0</div>
            </div>
            <div
              className={`sub-track music-sub ${music?.enabled ? "filled" : "empty-track"}`}
              style={{ position: "absolute", left: 0, right: 0, top: 4, height: audioBarH, zIndex: 2 }}
              onClick={(e) => {
                if (e.target === e.currentTarget) onFocusTrack?.("music");
              }}
            >
              {music?.enabled ? (
                <MusicBar
                  music={music}
                  totalForBar={totalForBar}
                  pxPerSec={pxPerSec}
                  xToSec={xToSec}
                  snap={snap}
                  onFocus={() => onFocusTrack?.("music")}
                  onPatch={onPatchMusic}
                  buildSnapTargets={(excl) =>
                    buildSnapTargets(layout, fps, playheadSec, totalForBar, targetSec, excl)
                  }
                />
              ) : (
                <span className="sub-track-hint" onClick={() => onFocusTrack?.("music")}>＋ Music-Bed hinzufügen</span>
              )}
            </div>
          </div>

          {/* ----- Voiceover multi-lane (mehrere Schnitt-Segmente möglich) ----- */}
          <div
            className="track-row scene-track-row multi-lane sub-multi-track voiceover-multi-lane"
            style={{ height: audioSubHeight, position: "relative" }}
          >
            <div className="scene-lane base-lane" style={{ height: audioLaneH, top: 0 }}>
              <div className="lane-tag">V0</div>
            </div>
            <div
              className={`sub-track voiceover-sub ${voiceover?.enabled && voiceover.src ? "filled" : "empty-track"}`}
              style={{ position: "absolute", left: 0, right: 0, top: 4, height: audioBarH, zIndex: 2 }}
              onClick={(e) => {
                if (e.target === e.currentTarget) onFocusTrack?.("voiceover");
              }}
            >
              {voiceover?.src && voiceover.enabled ? (
                (voiceover.segments && voiceover.segments.length > 0
                  ? voiceover.segments
                  : []
                ).map((seg) => (
                  <VoiceoverSegmentBar
                    key={seg.id}
                    seg={seg}
                    voiceover={voiceover}
                    totalForBar={totalForBar}
                    pxPerSec={pxPerSec}
                    xToSec={xToSec}
                    snap={snap}
                    selected={selectedUid === `vo:${seg.id}`}
                    onSelect={() => onSelect(`vo:${seg.id}`)}
                    onFocus={() => onFocusTrack?.("voiceover")}
                    onPatch={(p) => onPatchVoiceoverSegment?.(seg.id, p)}
                    onRemove={() => onRemoveVoiceoverSegment?.(seg.id)}
                    onSplitAtPlayhead={onSplitVoiceoverAtPlayhead}
                    playheadSec={playheadSec}
                    buildSnapTargets={(excl) =>
                      buildSnapTargets(layout, fps, playheadSec, totalForBar, targetSec, excl)
                    }
                  />
                ))
              ) : (
                <span className="sub-track-hint" onClick={() => onFocusTrack?.("voiceover")}>
                  ＋ Voiceover hochladen — eigene Sprachspur (eigene Lautstärke + Schnitt)
                </span>
              )}
            </div>
          </div>

          {/* ----- Watermark multi-lane ----- */}
          <div
            className="track-row scene-track-row multi-lane sub-multi-track"
            style={{ height: audioSubHeight, position: "relative" }}
          >
            <div className="scene-lane base-lane" style={{ height: audioLaneH, top: 0 }}>
              <div className="lane-tag">W0</div>
            </div>
            <div
              className="sub-track watermark-sub"
              onClick={() => onFocusTrack?.("watermark")}
              style={{ position: "absolute", left: 0, right: 0, top: 4, height: audioBarH, zIndex: 2 }}
            >
              {watermark?.enabled ? (
                watermarkSegments.length > 0 ? (
                  watermarkSegments.map((seg) => (
                    <div
                      key={seg.uid}
                      className="watermark-seg"
                      style={{
                        left: `${seg.leftPct}%`,
                        width: `${seg.widthPct}%`,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(seg.uid);
                        onFocusTrack?.("watermark");
                      }}
                      title={`Logo auf dieser Szene (${watermark.position.toUpperCase()}, ${Math.round(watermark.opacity * 100)}%) · Klick = Szene auswählen, Panel öffnet sich`}
                    >
                      <span className={`wm-corner ${watermark.position}`} />
                    </div>
                  ))
                ) : (
                  <span className="sub-track-hint">Logo aktiv, aber auf allen Szenen ausgeblendet</span>
                )
              ) : (
                <span className="sub-track-hint">＋ Logo / Watermark aktivieren</span>
              )}
            </div>
          </div>

          {/* ----- SFX multi-lane (independent SFX events as draggable bars) ----- */}
          <div
            className={`track-row scene-track-row multi-lane sub-multi-track sfx-multi-lane ${isSfxLibDragOver ? "drop-target-active" : ""}`}
            style={{ height: audioSubHeight, position: "relative" }}
            onDragOver={(e) => {
              if (!onDropSfxOnTimeline) return;
              if (!e.dataTransfer.types.includes(SFX_DRAG_MIME)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
              setIsSfxLibDragOver(true);
            }}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
              setIsSfxLibDragOver(false);
            }}
            onDrop={(e) => {
              if (!onDropSfxOnTimeline) return;
              const raw = e.dataTransfer.getData(SFX_DRAG_MIME);
              if (!raw) return;
              e.preventDefault();
              setIsSfxLibDragOver(false);
              let payload: {
                sound?: SfxName;
                defaultDurSec?: number;
                custom?: { url?: string; assetId?: string; displayName?: string };
              } = {};
              try {
                payload = JSON.parse(raw);
              } catch {
                return;
              }
              if (!payload.sound) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const xInTrack = e.clientX - rect.left;
              const fromSec = Math.max(0, (xInTrack / rect.width) * totalForBar);
              const durSec = Math.max(0.2, payload.defaultDurSec ?? 0.6);
              if (payload.custom?.url) {
                onDropSfxOnTimeline(payload.sound, {
                  fromSec,
                  durSec,
                  custom: {
                    src: payload.custom.url,
                    assetId: payload.custom.assetId,
                    label: payload.custom.displayName,
                  },
                });
              } else {
                onDropSfxOnTimeline(payload.sound, { fromSec, durSec });
              }
            }}
          >
            <div className="scene-lane base-lane" style={{ height: audioLaneH, top: 0 }}>
              <div className="lane-tag">S0</div>
            </div>
            <div
              className="sub-track sfx-sub"
              style={{ position: "absolute", left: 0, right: 0, top: 4, height: audioBarH, zIndex: 2 }}
            >
              {embeddedSfxChips.map((chip) => (
                <EmbeddedSfxChip
                  key={chip.uid}
                  chip={chip}
                  totalForBar={totalForBar}
                  chipHeight={audioBarH}
                  selected={selectedUid === chip.uid}
                  onSelect={() => onSelect(chip.uid)}
                  onRemove={() => {
                    if (chip.kind === "beat") {
                      onSuppressEmbeddedSfxBeat?.(chip.sceneUid, chip.embedIndex);
                    } else {
                      onMuteEmbeddedSceneBundledSfx?.(chip.sceneUid);
                    }
                  }}
                />
              ))}
              {sfxEvents.map((s) => (
                <SfxChip
                  key={s.uid}
                  sfxEvent={s}
                  totalForBar={totalForBar}
                  chipHeight={audioBarH}
                  selected={selectedUid === s.uid}
                  pxPerSec={pxPerSec}
                  xToSec={xToSec}
                  snap={snap}
                  onSelect={() => onSelect(s.uid)}
                  onPatch={(patch) => onPatchSfxEventTime?.(s.uid, patch)}
                  onRemove={() => onRemoveSfxEvent?.(s.uid)}
                  onDuplicate={() => onDuplicateSfxEvent?.(s.uid)}
                  onDragChange={setIsSfxDragging}
                  buildSnapTargets={(excludeUid) =>
                    buildSnapTargets(layout, fps, playheadSec, totalForBar, targetSec, excludeUid)
                  }
                />
              ))}
              {embeddedSfxChips.length === 0 && sfxEvents.length === 0 ? (
                <span className="sub-track-hint">
                  {isSfxLibDragOver
                    ? "Hier loslassen — SFX einfügen"
                    : "＋ Szene-SFX (Vorlage) erscheinen automatisch · eigene SFX aus der Galerie ziehen"}
                </span>
              ) : null}
            </div>
          </div>

          {/* ----- Overlays multi-lane (text / shape / icon / badge / image) ----- */}
          <div
            className="track-row scene-track-row multi-lane sub-multi-track overlays-multi-lane"
            style={{ height: overlayAreaHeight, position: "relative" }}
            onDragOver={(e) => {
              if (!onDropElementOnTimeline) return;
              const hasElement = e.dataTransfer.types.includes(ELEMENT_DRAG_MIME);
              if (!hasElement) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
              const rect = e.currentTarget.getBoundingClientRect();
              const yFromBottom = rect.bottom - e.clientY;
              const laneIdx = Math.max(
                0,
                Math.min(overlayLaneCount - 1, Math.floor(yFromBottom / SUB_LANE_H)),
              );
              setIsElementDragOver({ laneIdx });
            }}
            onDragLeave={(e) => {
              // Only clear when truly leaving the whole multi-lane block.
              if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
              setIsElementDragOver(null);
            }}
            onDrop={(e) => {
              if (!onDropElementOnTimeline) return;
              const raw = e.dataTransfer.getData(ELEMENT_DRAG_MIME);
              if (!raw) return;
              e.preventDefault();
              let payload: { elementId?: string } = {};
              try {
                payload = JSON.parse(raw);
              } catch {
                return;
              }
              if (!payload.elementId) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const xInTrack = e.clientX - rect.left;
              const fromSec = Math.max(
                0,
                (xInTrack / rect.width) * totalForBar,
              );
              const yFromBottom = rect.bottom - e.clientY;
              const laneIdx = Math.max(
                0,
                Math.min(overlayLaneCount - 1, Math.floor(yFromBottom / SUB_LANE_H)),
              );
              const track = laneToTrack(laneIdx);
              onDropElementOnTimeline(payload.elementId, { fromSec, track });
              setIsElementDragOver(null);
            }}
          >
            {overlayLanes.map((laneIdx) => (
              <div
                key={laneIdx}
                className={`scene-lane ${laneIdx === 0 ? "base-lane" : "overlay-lane"} ${
                  isElementDragOver?.laneIdx === laneIdx ? "drop-target" : ""
                }`}
                style={{ height: SUB_LANE_H, bottom: laneIdx * SUB_LANE_H }}
              >
                <div className="lane-tag">O{laneIdx}</div>
              </div>
            ))}
            {overlays.length === 0 ? (
              <div className="multi-lane-empty">
                ＋ Element-Library → in Vorschau ziehen oder „T" für Text
              </div>
            ) : (
              overlays
                .slice()
                .filter((o) => o.kind !== "group")
                .sort((a, b) => a.fromSec - b.fromSec)
                .map((o) => {
                  const displayLane = trackToLane.get(o.track) ?? 0;
                  const chipBottom = displayLane * SUB_LANE_H + 4;
                  return (
                    <OverlayChip
                      key={o.uid}
                      overlay={o}
                      totalForBar={totalForBar}
                      bottomPx={chipBottom}
                      chipHeight={SUB_LANE_H - 8}
                      displayLane={displayLane}
                      maxDisplayLane={overlayLaneCount - 1}
                      laneHeight={SUB_LANE_H}
                      laneToTrack={laneToTrack}
                      onDragChange={setIsOverlayDragging}
                      onPatchTrack={(t) => onPatchOverlayTime?.(o.uid, { track: t })}
                      pxPerSec={pxPerSec}
                      xToSec={xToSec}
                      snap={snap}
                      selected={selectedUid === o.uid}
                      onSelect={() => onSelect(o.uid)}
                      onPatch={(patch) => onPatchOverlayTime?.(o.uid, patch)}
                      onRemove={() => onRemoveOverlay?.(o.uid)}
                      onDuplicate={() => onDuplicateOverlay?.(o.uid)}
                      buildSnapTargets={(excludeUid) =>
                        buildSnapTargets(layout, fps, playheadSec, totalForBar, targetSec, excludeUid)
                      }
                    />
                  );
                })
            )}
          </div>
         </div>
        </div>
      </div>

      {/* ----- Right-click context menu (portal-style absolute on viewport) ----- */}
      {ctxMenu && ctxMenuScene && (
        <div
          className="timeline-ctx-menu"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            className="ctx-item"
            onClick={() => {
              onDuplicate(ctxMenu.uid);
              setCtxMenu(null);
            }}
          >
            <span className="ctx-icon">⎘</span> Duplizieren
            <span className="ctx-shortcut">⌘D</span>
          </button>
          {onSplit && (
            <button
              className="ctx-item"
              onClick={() => {
                onSplit(ctxMenu.uid);
                setCtxMenu(null);
              }}
              disabled={
                playheadSec <= (ctxMenuScene.fromSec ?? 0) + 0.05 ||
                playheadSec >= (ctxMenuScene.fromSec ?? 0) + ctxMenuScene.durationSec - 0.05
              }
            >
              <span className="ctx-icon">✂</span> Bei Playhead splitten
              <span className="ctx-shortcut">S</span>
            </button>
          )}
          {onMoveSceneSequence ? (
            <>
              <div className="ctx-sep" />
              <div className="ctx-submenu-label">Reihenfolge</div>
              <div className="ctx-track-row">
                <button
                  className="ctx-track-btn"
                  title="Eine Position nach vorne (früher abspielen)"
                  disabled={
                    // First-in-time clip can't move further forward.
                    Math.min(
                      ...scenes.map((s) => s.fromSec ?? 0),
                    ) === (ctxMenuScene.fromSec ?? 0)
                  }
                  onClick={() => {
                    onMoveSceneSequence(ctxMenu.uid, "up");
                    setCtxMenu(null);
                  }}
                >
                  ↑ Früher
                </button>
                <button
                  className="ctx-track-btn"
                  title="Eine Position nach hinten (später abspielen)"
                  disabled={
                    Math.max(
                      ...scenes.map((s) => s.fromSec ?? 0),
                    ) === (ctxMenuScene.fromSec ?? 0)
                  }
                  onClick={() => {
                    onMoveSceneSequence(ctxMenu.uid, "down");
                    setCtxMenu(null);
                  }}
                >
                  ↓ Später
                </button>
              </div>
            </>
          ) : null}
          <div className="ctx-sep" />
          <div className="ctx-submenu-label">Auf Track…</div>
          <div className="ctx-track-row">
            {Array.from({ length: Math.max(3, maxTrack + 2) }).map((_, i) => (
              <button
                key={i}
                className={`ctx-track-btn ${(ctxMenuScene.track ?? 0) === i ? "active" : ""}`}
                onClick={() => {
                  onPatchTime(ctxMenu.uid, { track: i });
                  setCtxMenu(null);
                }}
              >
                L{i}
              </button>
            ))}
          </div>
          <div className="ctx-sep" />
          <button
            className="ctx-item danger"
            onClick={() => {
              onRemove(ctxMenu.uid);
              setCtxMenu(null);
            }}
          >
            <span className="ctx-icon">✕</span> Löschen
            <span className="ctx-shortcut">⌫</span>
          </button>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// FREEFORM CLIP — absolute-positioned, drag to move, edge-handles to resize.
// =============================================================================

type ClipDrag =
  | {
      kind: "move";
      startClientX: number;
      startClientY: number;
      startFromSec: number;
      startTrack: number;
    }
  | {
      kind: "resize-l";
      startClientX: number;
      startFromSec: number;
      startDur: number;
      startNaturalDur: number;
    }
  | {
      kind: "resize-r";
      startClientX: number;
      startFromSec: number;
      startDur: number;
      startNaturalDur: number;
    };

/**
 * Berechnet, wie sich `naturalDurationSec` und `playbackRate` beim Resize
 * eines Clips verhalten sollen.
 *
 * - SPEED-MODE (default, `trim=false`): die authored Animations-Länge bleibt
 *   verankert. `playbackRate` wird so gesetzt, dass die Animation das
 *   neue Timeline-Fenster exakt füllt — Animation wird langsamer beim
 *   Strecken, schneller beim Stauchen, kein Standbild am Ende.
 * - TRIM-MODE (`trim=true`, ⌥/Alt held): altes Verhalten — `playbackRate`
 *   bleibt 1, `naturalDurationSec` bleibt unverändert, das letzte Frame
 *   wird gehalten / abgeschnitten.
 */
function computeResizePatch(args: {
  nextDur: number;
  startDur: number;
  startNaturalDur: number;
  trim: boolean;
}): { naturalDurationSec: number | undefined; playbackRate: number | undefined } {
  const { nextDur, startNaturalDur, trim } = args;
  if (trim) {
    return { naturalDurationSec: startNaturalDur, playbackRate: undefined };
  }
  const safeDur = Math.max(MIN_DUR_SEC, nextDur);
  const safeNatural = Math.max(MIN_DUR_SEC, startNaturalDur);
  const rawRate = safeNatural / safeDur;
  const rate = Math.min(4, Math.max(0.25, Math.round(rawRate * 100) / 100));
  const isOne = Math.abs(rate - 1) < 0.005;
  return {
    // Anker fixieren, sobald der User strecht/staucht — sonst würde die
    // nächste Resize-Aktion vom neuen Timeline-Slot ausgehen statt von
    // der ursprünglichen authored Animations-Länge.
    naturalDurationSec: isOne ? undefined : Math.round(safeNatural * 10) / 10,
    playbackRate: isOne ? undefined : rate,
  };
}

function formatRate(rate: number | undefined): string {
  if (rate == null || Math.abs(rate - 1) < 0.005) return "1.00×";
  return `${rate.toFixed(2)}×`;
}

const FreeformClip: React.FC<{
  scene: ReelScene;
  /** Position resolved by `resolveClipLayout` (works for sequential reels too). */
  resolvedFromSec: number;
  resolvedDurSec: number;
  resolvedTrack: number;
  label: string;
  look: string;
  leftPct: number;
  widthPct: number;
  bottomPx: number;
  laneHeight: number;
  selected: boolean;
  trackAreaRef: React.RefObject<HTMLDivElement | null>;
  totalForBar: number;
  maxTrack: number;
  fps: number;
  layout: ClipLayout[];
  playheadSec: number;
  targetSec: number;
  snap: boolean;
  xToSec: (clientX: number) => number;
  pxPerSec: () => number;
  onSelect: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onPatch: (patch: { fromSec?: number; durationSec?: number; naturalDurationSec?: number; track?: number }) => void;
  onContextMenu: (clientX: number, clientY: number) => void;
  onDragHud: React.Dispatch<
    React.SetStateAction<
      | null
      | {
          snapSec: number | null;
          snapKind: SnapTarget["kind"] | null;
          targetTrack: number | null;
          tooltip: { x: number; y: number; text: string } | null;
        }
    >
  >;
}> = ({
  scene,
  resolvedFromSec,
  resolvedDurSec,
  resolvedTrack,
  label,
  look,
  leftPct,
  widthPct,
  bottomPx,
  laneHeight,
  selected,
  trackAreaRef,
  totalForBar,
  maxTrack,
  fps,
  layout,
  playheadSec,
  targetSec,
  snap,
  xToSec,
  pxPerSec,
  onSelect,
  onRemove,
  onDuplicate,
  onPatch,
  onContextMenu,
  onDragHud,
}) => {
  const [drag, setDrag] = useState<ClipDrag | null>(null);

  // Latest props captured in refs so the long-lived window listeners always
  // see fresh data without re-attaching on every render.
  const propsRef = useRef({
    snap,
    layout,
    fps,
    playheadSec,
    totalForBar,
    targetSec,
    sceneUid: scene.uid,
    sceneTrack: scene.track ?? resolvedTrack,
    sceneDur: scene.durationSec,
    laneHeight,
    maxTrack,
    pxPerSec,
    xToSec,
    onPatch,
    onDragHud,
    resolvedTrack,
  });
  propsRef.current = {
    snap,
    layout,
    fps,
    playheadSec,
    totalForBar,
    targetSec,
    sceneUid: scene.uid,
    sceneTrack: scene.track ?? resolvedTrack,
    sceneDur: scene.durationSec,
    laneHeight,
    maxTrack,
    pxPerSec,
    xToSec,
    onPatch,
    onDragHud,
    resolvedTrack,
  };

  /**
   * Begin a drag (move / resize-l / resize-r). Attaches `mousemove` /
   * `mouseup` listeners to `window` IMMEDIATELY (synchronously) so a fast
   * click that releases before the next render still gets its mouseup
   * handled and clears the drag state. This was the cause of "stuck drag
   * after a single click" — using useEffect attached the listeners too late.
   */
  const beginDrag = useCallback((d: ClipDrag): void => {
    setDrag(d);
    let raf = 0;
    let lastEvent: MouseEvent | null = null;

    const flush = () => {
      raf = 0;
      const e = lastEvent;
      if (!e) return;
      const p = propsRef.current;

      const maybeSnapLocal = (
        sec: number,
      ): { sec: number; kind: SnapTarget["kind"] | null } => {
        if (!p.snap) return { sec, kind: null };
        const targets = buildSnapTargets(
          p.layout,
          p.fps,
          p.playheadSec,
          p.totalForBar,
          p.targetSec,
          p.sceneUid,
        );
        const r = applySnap(sec, targets, p.pxPerSec());
        return { sec: r.sec, kind: r.hit ? r.hit.kind : null };
      };

      if (d.kind === "move") {
        const dxSec = p.xToSec(e.clientX) - p.xToSec(d.startClientX);
        const candidate = Math.max(0, d.startFromSec + dxSec);
        const snapped = maybeSnapLocal(candidate);
        const dyPx = e.clientY - d.startClientY;
        const trackDelta = -Math.round(dyPx / (p.laneHeight + 8));
        const nextTrack = Math.max(0, Math.min(p.maxTrack, d.startTrack + trackDelta));
        p.onPatch({ fromSec: snapped.sec, track: nextTrack });
        p.onDragHud({
          snapSec: snapped.kind ? snapped.sec : null,
          snapKind: snapped.kind,
          targetTrack: nextTrack,
          tooltip: {
            x: e.clientX,
            y: e.clientY,
            text: `${snapped.sec.toFixed(2)}s → ${(snapped.sec + p.sceneDur).toFixed(2)}s · L${nextTrack}`,
          },
        });
      } else if (d.kind === "resize-r") {
        const dxSec = p.xToSec(e.clientX) - p.xToSec(d.startClientX);
        const rawEnd = d.startFromSec + d.startDur + dxSec;
        const snapEnd = maybeSnapLocal(rawEnd);
        const nextDur = Math.max(MIN_DUR_SEC, snapEnd.sec - d.startFromSec);
        // SPEED-MODE (default): die authored Animationslänge bleibt fest
        // verankert (`naturalDurationSec`); `playbackRate` wird so gesetzt,
        // dass die Animation das gewählte Timeline-Fenster exakt füllt —
        // kein Fade-Out, kein Standbild am Ende.
        // TRIM-MODE (⌥/Alt): altes Verhalten — Animation läuft in ihrer
        // natural pace, danach friert das letzte Frame ein.
        const patch = computeResizePatch({
          nextDur,
          startDur: d.startDur,
          startNaturalDur: d.startNaturalDur,
          trim: e.altKey,
        });
        p.onPatch({ durationSec: nextDur, ...patch });
        p.onDragHud({
          snapSec: snapEnd.kind ? snapEnd.sec : null,
          snapKind: snapEnd.kind,
          targetTrack: p.sceneTrack,
          tooltip: {
            x: e.clientX,
            y: e.clientY,
            text: `${d.startFromSec.toFixed(2)}s → ${(d.startFromSec + nextDur).toFixed(2)}s · ${nextDur.toFixed(2)}s${e.altKey ? " · trim" : ` · ${formatRate(patch.playbackRate)}`}`,
          },
        });
      } else if (d.kind === "resize-l") {
        const dxSec = p.xToSec(e.clientX) - p.xToSec(d.startClientX);
        const rawStart = d.startFromSec + dxSec;
        const snapStart = maybeSnapLocal(rawStart);
        const nextFrom = Math.max(
          0,
          Math.min(d.startFromSec + d.startDur - MIN_DUR_SEC, snapStart.sec),
        );
        const nextDur = Math.max(MIN_DUR_SEC, d.startDur - (nextFrom - d.startFromSec));
        const patch = computeResizePatch({
          nextDur,
          startDur: d.startDur,
          startNaturalDur: d.startNaturalDur,
          trim: e.altKey,
        });
        p.onPatch({ fromSec: nextFrom, durationSec: nextDur, ...patch });
        p.onDragHud({
          snapSec: snapStart.kind ? snapStart.sec : null,
          snapKind: snapStart.kind,
          targetTrack: p.sceneTrack,
          tooltip: {
            x: e.clientX,
            y: e.clientY,
            text: `${nextFrom.toFixed(2)}s → ${(nextFrom + nextDur).toFixed(2)}s · ${nextDur.toFixed(2)}s${e.altKey ? " · trim" : ` · ${formatRate(patch.playbackRate)}`}`,
          },
        });
      }
    };

    const onMove = (e: MouseEvent) => {
      lastEvent = e;
      if (!raf) raf = window.requestAnimationFrame(flush);
    };
    const onUp = () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDrag(null);
      propsRef.current.onDragHud(null);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  const startMove = (e: React.MouseEvent): void => {
    if (e.button !== 0) return; // ignore right-click
    e.stopPropagation();
    onSelect();
    beginDrag({
      kind: "move",
      startClientX: e.clientX,
      startClientY: e.clientY,
      startFromSec: scene.fromSec ?? resolvedFromSec,
      startTrack: scene.track ?? resolvedTrack,
    });
  };

  const fmtRange = `${(scene.fromSec ?? resolvedFromSec).toFixed(2)}s → ${((scene.fromSec ?? resolvedFromSec) + scene.durationSec).toFixed(2)}s`;

  return (
    <div
      className={`clip freeform-clip ${selected ? "selected" : ""} ${drag ? `dragging drag-${drag.kind}` : ""}`}
      data-look={look}
      style={{
        position: "absolute",
        left: `${leftPct}%`,
        width: `${widthPct}%`,
        bottom: bottomPx,
        height: laneHeight,
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      onDoubleClick={(e) => {
        // Double-click → seek playhead to clip start (Final Cut style).
        e.stopPropagation();
        // No direct seek here; selecting fires inspector focus already.
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu(e.clientX, e.clientY);
      }}
      onMouseDown={startMove}
      title={`${label} · ${fmtRange}`}
    >
      {/* Left resize handle */}
      <div
        className="clip-edge clip-edge-l"
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          e.stopPropagation();
          onSelect();
          beginDrag({
            kind: "resize-l",
            startClientX: e.clientX,
            startFromSec: scene.fromSec ?? resolvedFromSec,
            startDur: scene.durationSec,
            startNaturalDur: scene.naturalDurationSec ?? scene.durationSec,
          });
        }}
        title="Anfang ziehen — Animation skaliert auf das Fenster · ⌥ ziehen: trimmen + halten"
      />
      <div className="clip-body">
        <div className="clip-title">{label}</div>
        <div className="clip-meta">
          <span>{scene.durationSec.toFixed(1)}s</span>
          {scene.playbackRate != null && Math.abs(scene.playbackRate - 1) > 0.02 ? (
            <>
              <span className="clip-meta-sep">·</span>
              <span title="Clip-Geschwindigkeit">{scene.playbackRate.toFixed(2)}×</span>
            </>
          ) : null}
          <span className="clip-meta-sep">·</span>
          <span>L{scene.track ?? resolvedTrack}</span>
        </div>
      </div>
      <div className="clip-actions" onMouseDown={(e) => e.stopPropagation()}>
        <button
          className="icon-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          title="Duplizieren (⌘D)"
        >
          ⎘
        </button>
        <button
          className="icon-btn danger"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          title="Löschen (⌫)"
        >
          ✕
        </button>
      </div>
      {/* Right resize handle */}
      <div
        className="clip-edge clip-edge-r"
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          e.stopPropagation();
          onSelect();
          beginDrag({
            kind: "resize-r",
            startClientX: e.clientX,
            startFromSec: scene.fromSec ?? resolvedFromSec,
            startDur: scene.durationSec,
            startNaturalDur: scene.naturalDurationSec ?? scene.durationSec,
          });
        }}
        title="Länge ziehen — Animation skaliert auf das Fenster · ⌥ ziehen: trimmen + halten"
      />
      {/* Live drag tooltip — anchored to mouse via portal-ish fixed div */}
      {drag && (
        <DragTooltip
          text={`${(scene.fromSec ?? resolvedFromSec).toFixed(2)}s · ${scene.durationSec.toFixed(2)}s · L${scene.track ?? resolvedTrack}`}
        />
      )}
    </div>
  );
};

// Tiny floating tooltip rendered at top-right of the clip (avoids needing
// portals + global mouse tracking just for the label).
const DragTooltip: React.FC<{ text: string }> = ({ text }) => (
  <div className="clip-drag-tooltip">{text}</div>
);

// =============================================================================
// OVERLAY CHIP — compact clip representation for the overlays sub-track.
// Supports click-to-select, drag-to-move (time), edge-drag-to-resize and a
// minimal right-click context menu (duplicate / delete). Uses the same
// snap-target machinery as the scene clips so users get magnetic alignment
// to the playhead, grid lines, and other clip edges.
// =============================================================================

type OverlayDrag =
  | {
      kind: "move";
      startClientX: number;
      startClientY: number;
      startFromSec: number;
      startDisplayLane: number;
    }
  | { kind: "resize-l"; startClientX: number; startFromSec: number; startDur: number }
  | { kind: "resize-r"; startClientX: number; startFromSec: number; startDur: number };

const OVERLAY_MIN_DUR = 0.4;

const OverlayChip: React.FC<{
  overlay: OverlayLayer;
  totalForBar: number;
  bottomPx: number;
  chipHeight: number;
  /** Current DENSE lane index (0..maxDisplayLane). */
  displayLane: number;
  /** Max dense lane index the chip can be dragged to (grows during drag). */
  maxDisplayLane: number;
  /** Pixel height of one overlay lane (used to translate dy → lane delta). */
  laneHeight: number;
  /** Convert a dense lane index → real `track` value. */
  laneToTrack: (laneIdx: number) => number;
  pxPerSec: () => number;
  xToSec: (clientX: number) => number;
  snap: boolean;
  selected: boolean;
  onSelect: () => void;
  onPatch: (patch: { fromSec?: number; durSec?: number }) => void;
  /** Patch only the track index (separate because it lives on the overlay base). */
  onPatchTrack: (track: number) => void;
  /** Tell parent timeline whether this chip is currently being dragged so it
   *  can reveal an extra empty lane (CapCut-style). */
  onDragChange: (dragging: boolean) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  buildSnapTargets: (excludeUid: string | null) => SnapTarget[];
}> = ({
  overlay,
  totalForBar,
  bottomPx,
  chipHeight,
  displayLane,
  maxDisplayLane,
  laneHeight,
  laneToTrack,
  pxPerSec,
  xToSec,
  snap,
  selected,
  onSelect,
  onPatch,
  onPatchTrack,
  onDragChange,
  onRemove,
  onDuplicate,
  buildSnapTargets,
}) => {
  const [drag, setDrag] = useState<OverlayDrag | null>(null);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);

  // Use a propsRef so listeners attached during a drag always see fresh props.
  const propsRef = useRef({
    overlay,
    totalForBar,
    pxPerSec,
    xToSec,
    snap,
    onPatch,
    onPatchTrack,
    maxDisplayLane,
    laneHeight,
    laneToTrack,
    buildSnapTargets,
  });
  propsRef.current = {
    overlay,
    totalForBar,
    pxPerSec,
    xToSec,
    snap,
    onPatch,
    onPatchTrack,
    maxDisplayLane,
    laneHeight,
    laneToTrack,
    buildSnapTargets,
  };

  const beginDrag = useCallback((d: OverlayDrag) => {
    setDrag(d);
    onDragChange(true);
    const onMove = (e: MouseEvent) => {
      const p = propsRef.current;
      const dxSec = p.xToSec(e.clientX) - p.xToSec(d.startClientX);
      const maybeSnap = (sec: number): number => {
        if (!p.snap) return sec;
        const targets = p.buildSnapTargets(p.overlay.uid);
        const r = applySnap(sec, targets, p.pxPerSec());
        return r.sec;
      };
      if (d.kind === "move") {
        const next = Math.max(0, maybeSnap(d.startFromSec + dxSec));
        p.onPatch({ fromSec: next });
        const dyPx = e.clientY - d.startClientY;
        const laneDelta = -Math.round(dyPx / p.laneHeight);
        const nextLane = Math.max(
          0,
          Math.min(p.maxDisplayLane, d.startDisplayLane + laneDelta),
        );
        const nextTrack = p.laneToTrack(nextLane);
        if (nextTrack !== p.overlay.track) p.onPatchTrack(nextTrack);
      } else if (d.kind === "resize-l") {
        const rawStart = d.startFromSec + dxSec;
        const snapStart = maybeSnap(rawStart);
        const end = d.startFromSec + d.startDur;
        const newStart = Math.max(0, Math.min(snapStart, end - OVERLAY_MIN_DUR));
        p.onPatch({ fromSec: newStart, durSec: end - newStart });
      } else if (d.kind === "resize-r") {
        const rawEnd = d.startFromSec + d.startDur + dxSec;
        const snapEnd = maybeSnap(rawEnd);
        const newDur = Math.max(OVERLAY_MIN_DUR, snapEnd - d.startFromSec);
        p.onPatch({ durSec: newDur });
      }
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDrag(null);
      onDragChange(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [onDragChange]);

  // Close ctx menu on outside click / Esc.
  useEffect(() => {
    if (!ctx) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest(".overlay-chip-ctx")) return;
      setCtx(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCtx(null);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [ctx]);

  const leftPct = (overlay.fromSec / totalForBar) * 100;
  const widthPct = Math.max(0.5, (overlay.durSec / totalForBar) * 100);
  const badge = overlayChipBadge(overlay.kind);
  const label = overlayChipText(overlay);

  return (
    <div
      className={`overlay-chip kind-${badge.tone} ${selected ? "selected" : ""} ${drag ? "dragging" : ""}`}
      style={{ left: `${leftPct}%`, width: `${widthPct}%`, bottom: bottomPx, height: chipHeight }}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        onSelect();
        beginDrag({
          kind: "move",
          startClientX: e.clientX,
          startClientY: e.clientY,
          startFromSec: overlay.fromSec,
          startDisplayLane: displayLane,
        });
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect();
        setCtx({ x: e.clientX, y: e.clientY });
      }}
      title={`${badge.label}: ${label} · ${overlay.fromSec.toFixed(2)}s · ${overlay.durSec.toFixed(2)}s`}
    >
      <div
        className="overlay-chip-edge l"
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          e.stopPropagation();
          onSelect();
          beginDrag({
            kind: "resize-l",
            startClientX: e.clientX,
            startFromSec: overlay.fromSec,
            startDur: overlay.durSec,
          });
        }}
      />
      <span className="overlay-chip-badge">{badge.glyph}</span>
      <span className="overlay-chip-label">{label}</span>
      <div
        className="overlay-chip-edge r"
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          e.stopPropagation();
          onSelect();
          beginDrag({
            kind: "resize-r",
            startClientX: e.clientX,
            startFromSec: overlay.fromSec,
            startDur: overlay.durSec,
          });
        }}
      />
      {drag && (
        <div className="clip-drag-tooltip">
          {overlay.fromSec.toFixed(2)}s · {overlay.durSec.toFixed(2)}s
        </div>
      )}
      {ctx && (
        <div className="overlay-chip-ctx" style={{ left: ctx.x, top: ctx.y }} onMouseDown={(e) => e.stopPropagation()}>
          <button
            className="ctx-item"
            onClick={() => {
              onDuplicate();
              setCtx(null);
            }}
          >
            <span className="ctx-icon">⎘</span> Duplizieren
            <span className="ctx-shortcut">⌘D</span>
          </button>
          <div className="ctx-sep" />
          <button
            className="ctx-item danger"
            onClick={() => {
              onRemove();
              setCtx(null);
            }}
          >
            <span className="ctx-icon">✕</span> Löschen
            <span className="ctx-shortcut">⌫</span>
          </button>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MUSIC BAR — draggable + resizable + fade-handles representation of the
// background music track on the M0 sub-lane. Mirrors SfxChip's drag UX but
// lives in its own single lane and adds fade-in / fade-out triangle drags.
// =============================================================================

type MusicDrag =
  | { kind: "move";     startClientX: number; startFromSec: number }
  | { kind: "resize-l"; startClientX: number; startFromSec: number; startDur: number }
  | { kind: "resize-r"; startClientX: number; startFromSec: number; startDur: number }
  | { kind: "fade-in";  startClientX: number; startFade: number; durAtStart: number }
  | { kind: "fade-out"; startClientX: number; startFade: number; durAtStart: number };

const MUSIC_MIN_DUR = 0.5;

const MusicBar: React.FC<{
  music: MusicConfig;
  totalForBar: number;
  pxPerSec: () => number;
  xToSec: (clientX: number) => number;
  snap: boolean;
  onFocus: () => void;
  onPatch?: (patch: Partial<MusicConfig>) => void;
  buildSnapTargets: (excludeUid: string | null) => SnapTarget[];
}> = ({
  music,
  totalForBar,
  pxPerSec,
  xToSec,
  snap,
  onFocus,
  onPatch,
  buildSnapTargets,
}) => {
  const [drag, setDrag] = useState<MusicDrag | null>(null);

  const fromSec = music.fromSec ?? 0;
  const durSec = music.durSec ?? Math.max(MUSIC_MIN_DUR, totalForBar - fromSec);
  const fadeInSec = music.fadeInSec ?? 0;
  const fadeOutSec = music.fadeOutSec ?? 0;

  const propsRef = useRef({ music, pxPerSec, xToSec, snap, onPatch, buildSnapTargets, durSec });
  propsRef.current = { music, pxPerSec, xToSec, snap, onPatch, buildSnapTargets, durSec };

  const beginDrag = useCallback((d: MusicDrag) => {
    if (!onPatch) return;
    setDrag(d);
    const onMove = (e: MouseEvent) => {
      const p = propsRef.current;
      if (!p.onPatch) return;
      const dxSec = p.xToSec(e.clientX) - p.xToSec(d.startClientX);
      const maybeSnap = (sec: number): number => {
        if (!p.snap) return sec;
        const targets = p.buildSnapTargets(null);
        return applySnap(sec, targets, p.pxPerSec()).sec;
      };
      if (d.kind === "move") {
        const next = Math.max(0, maybeSnap(d.startFromSec + dxSec));
        p.onPatch({ fromSec: Math.round(next * 100) / 100 });
      } else if (d.kind === "resize-l") {
        const rawStart = d.startFromSec + dxSec;
        const snapStart = maybeSnap(rawStart);
        const end = d.startFromSec + d.startDur;
        const newStart = Math.max(0, Math.min(snapStart, end - MUSIC_MIN_DUR));
        p.onPatch({
          fromSec: Math.round(newStart * 100) / 100,
          durSec: Math.round((end - newStart) * 100) / 100,
        });
      } else if (d.kind === "resize-r") {
        const rawEnd = d.startFromSec + d.startDur + dxSec;
        const snapEnd = maybeSnap(rawEnd);
        const newDur = Math.max(MUSIC_MIN_DUR, snapEnd - d.startFromSec);
        p.onPatch({ durSec: Math.round(newDur * 100) / 100 });
      } else if (d.kind === "fade-in") {
        const next = Math.max(0, Math.min(d.durAtStart / 2, d.startFade + dxSec));
        p.onPatch({ fadeInSec: Math.round(next * 100) / 100 });
      } else if (d.kind === "fade-out") {
        const next = Math.max(0, Math.min(d.durAtStart / 2, d.startFade - dxSec));
        p.onPatch({ fadeOutSec: Math.round(next * 100) / 100 });
      }
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDrag(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [onPatch]);

  const leftPct = (fromSec / totalForBar) * 100;
  const widthPct = Math.max(1, (durSec / totalForBar) * 100);

  // Fade-handle horizontal positions (in % of bar width).
  const fadeInPct = (fadeInSec / Math.max(0.01, durSec)) * 100;
  const fadeOutPct = (fadeOutSec / Math.max(0.01, durSec)) * 100;

  const isCustom = !!music.src;
  const beatGlyph = isCustom ? "🎶" : "🎵";
  const label = music.label ?? music.bed ?? (isCustom ? "Custom" : "Music");
  const interactive = !!onPatch;

  return (
    <div
      className={`music-bar ${drag ? `dragging drag-${drag.kind}` : ""} ${interactive ? "interactive" : ""}`}
      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
      onMouseDown={(e) => {
        if (!interactive || e.button !== 0) return;
        e.stopPropagation();
        onFocus();
        beginDrag({
          kind: "move",
          startClientX: e.clientX,
          startFromSec: fromSec,
        });
      }}
      onClick={(e) => {
        e.stopPropagation();
        onFocus();
      }}
      title={`${label} · ${fromSec.toFixed(1)}s → ${(fromSec + durSec).toFixed(1)}s · Vol ${Math.round((music.volume ?? 0.18) * 100)}%`}
    >
      {/* Left resize edge */}
      {interactive && (
        <div
          className="music-bar-edge l"
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            onFocus();
            beginDrag({
              kind: "resize-l",
              startClientX: e.clientX,
              startFromSec: fromSec,
              startDur: durSec,
            });
          }}
          title="Anfang ziehen"
        />
      )}

      <span className="music-bar-bed">{beatGlyph} {label}</span>
      <span className="music-bar-vol">Vol {Math.round((music.volume ?? 0.18) * 100)}%</span>

      {music.src ? (
        <WaveformCanvas
          src={music.src}
          windowStartSec={music.trimStartSec ?? 0}
          windowDurSec={durSec}
          colorTop="rgba(216, 180, 254, 0.9)"
          colorBottom="rgba(124, 58, 237, 0.85)"
          className="music-bar-waveform"
        />
      ) : (
        <div className="music-wave">
          {Array.from({ length: 60 }).map((_, i) => (
            <span
              key={i}
              style={{
                height: `${20 + Math.abs(Math.sin(i * 0.7 + (label.charCodeAt(0) % 7))) * 60}%`,
              }}
            />
          ))}
        </div>
      )}

      {/* Fade-in shading + handle */}
      {fadeInSec > 0 && (
        <div className="music-bar-fade in" style={{ width: `${fadeInPct}%` }} aria-hidden />
      )}
      {interactive && (
        <div
          className="music-bar-fade-handle in"
          style={{ left: `${fadeInPct}%` }}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            onFocus();
            beginDrag({
              kind: "fade-in",
              startClientX: e.clientX,
              startFade: fadeInSec,
              durAtStart: durSec,
            });
          }}
          title={`Fade-In ${fadeInSec.toFixed(2)}s — ziehen zum Anpassen`}
        >
          <span className="music-bar-fade-knob">▶</span>
        </div>
      )}

      {/* Fade-out shading + handle */}
      {fadeOutSec > 0 && (
        <div className="music-bar-fade out" style={{ width: `${fadeOutPct}%` }} aria-hidden />
      )}
      {interactive && (
        <div
          className="music-bar-fade-handle out"
          style={{ right: `${fadeOutPct}%` }}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            onFocus();
            beginDrag({
              kind: "fade-out",
              startClientX: e.clientX,
              startFade: fadeOutSec,
              durAtStart: durSec,
            });
          }}
          title={`Fade-Out ${fadeOutSec.toFixed(2)}s — ziehen zum Anpassen`}
        >
          <span className="music-bar-fade-knob">◀</span>
        </div>
      )}

      {/* Right resize edge */}
      {interactive && (
        <div
          className="music-bar-edge r"
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            onFocus();
            beginDrag({
              kind: "resize-r",
              startClientX: e.clientX,
              startFromSec: fromSec,
              startDur: durSec,
            });
          }}
          title="Länge ziehen"
        />
      )}

      {drag && (
        <div className="clip-drag-tooltip">
          {drag.kind === "fade-in"
            ? `Fade-In ${fadeInSec.toFixed(2)}s`
            : drag.kind === "fade-out"
              ? `Fade-Out ${fadeOutSec.toFixed(2)}s`
              : `${fromSec.toFixed(2)}s → ${(fromSec + durSec).toFixed(2)}s`}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// VOICEOVER SEGMENT BAR — eine einzelne Bar pro VoiceoverSegment. Mehrere
// Segmente einer Spur teilen sich `voiceover.src` / `voiceover.volume` /
// `voiceover.label`, sind aber unabhängig auf der Timeline platziert.
// Echte Waveform via WebAudio + Canvas, Drag/Resize/Fade/Trim-Handles
// identisch zur alten VoiceoverBar, plus Selektion + Kontext-Menü
// (Splitten am Playhead, Löschen).
// =============================================================================

type VoiceoverDrag =
  | { kind: "move";       startClientX: number; startFromSec: number }
  | { kind: "resize-l";   startClientX: number; startFromSec: number; startDur: number; startTrim: number }
  | { kind: "resize-r";   startClientX: number; startFromSec: number; startDur: number }
  | { kind: "fade-in";    startClientX: number; startFade: number; durAtStart: number }
  | { kind: "fade-out";   startClientX: number; startFade: number; durAtStart: number }
  | { kind: "trim-start"; startClientX: number; startTrim: number; startFromSec: number; startDur: number };

const VOICEOVER_MIN_DUR = 0.2;

type VoSegPatch = Partial<{
  fromSec: number;
  durSec: number;
  trimStartSec: number;
  fadeInSec: number;
  fadeOutSec: number;
}>;

const VoiceoverSegmentBar: React.FC<{
  seg: VoiceoverSegment;
  voiceover: VoiceoverConfig;
  totalForBar: number;
  pxPerSec: () => number;
  xToSec: (clientX: number) => number;
  snap: boolean;
  selected: boolean;
  onSelect: () => void;
  onFocus: () => void;
  onPatch?: (patch: VoSegPatch) => void;
  onRemove?: () => void;
  onSplitAtPlayhead?: () => void;
  playheadSec: number;
  buildSnapTargets: (excludeUid: string | null) => SnapTarget[];
}> = ({
  seg,
  voiceover,
  totalForBar,
  pxPerSec,
  xToSec,
  snap,
  selected,
  onSelect,
  onFocus,
  onPatch,
  onRemove,
  onSplitAtPlayhead,
  playheadSec,
  buildSnapTargets,
}) => {
  const [drag, setDrag] = useState<VoiceoverDrag | null>(null);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!ctx) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest(".sfx-chip-ctx")) return;
      setCtx(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCtx(null);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [ctx]);

  const fromSec = seg.fromSec;
  const durSec = Math.max(VOICEOVER_MIN_DUR, seg.durSec);
  const fadeInSec = seg.fadeInSec ?? 0;
  const fadeOutSec = seg.fadeOutSec ?? 0;
  const trimStart = seg.trimStartSec ?? 0;

  const propsRef = useRef({ pxPerSec, xToSec, snap, onPatch, buildSnapTargets });
  propsRef.current = { pxPerSec, xToSec, snap, onPatch, buildSnapTargets };

  const beginDrag = useCallback((d: VoiceoverDrag) => {
    if (!onPatch) return;
    setDrag(d);
    const onMove = (e: MouseEvent) => {
      const p = propsRef.current;
      if (!p.onPatch) return;
      const dxSec = p.xToSec(e.clientX) - p.xToSec(d.startClientX);
      const maybeSnap = (sec: number): number => {
        if (!p.snap) return sec;
        const targets = p.buildSnapTargets(null);
        return applySnap(sec, targets, p.pxPerSec()).sec;
      };
      if (d.kind === "move") {
        const next = Math.max(0, maybeSnap(d.startFromSec + dxSec));
        p.onPatch({ fromSec: Math.round(next * 100) / 100 });
      } else if (d.kind === "resize-l") {
        const rawStart = d.startFromSec + dxSec;
        const snapStart = maybeSnap(rawStart);
        const end = d.startFromSec + d.startDur;
        const newStart = Math.max(0, Math.min(snapStart, end - VOICEOVER_MIN_DUR));
        const delta = newStart - d.startFromSec;
        const newDur = end - newStart;
        const newTrim = Math.max(0, d.startTrim + delta);
        p.onPatch({
          fromSec: Math.round(newStart * 100) / 100,
          durSec: Math.round(newDur * 100) / 100,
          trimStartSec: Math.round(newTrim * 100) / 100,
        });
      } else if (d.kind === "resize-r") {
        const rawEnd = d.startFromSec + d.startDur + dxSec;
        const snapEnd = maybeSnap(rawEnd);
        const newDur = Math.max(VOICEOVER_MIN_DUR, snapEnd - d.startFromSec);
        p.onPatch({ durSec: Math.round(newDur * 100) / 100 });
      } else if (d.kind === "fade-in") {
        const next = Math.max(0, Math.min(d.durAtStart / 2, d.startFade + dxSec));
        p.onPatch({ fadeInSec: Math.round(next * 100) / 100 });
      } else if (d.kind === "fade-out") {
        const next = Math.max(0, Math.min(d.durAtStart / 2, d.startFade - dxSec));
        p.onPatch({ fadeOutSec: Math.round(next * 100) / 100 });
      } else if (d.kind === "trim-start") {
        const next = Math.max(0, d.startTrim + dxSec);
        p.onPatch({ trimStartSec: Math.round(next * 100) / 100 });
      }
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDrag(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [onPatch]);

  const leftPct = (fromSec / totalForBar) * 100;
  const widthPct = Math.max(0.5, (durSec / totalForBar) * 100);
  const fadeInPct = (fadeInSec / Math.max(0.01, durSec)) * 100;
  const fadeOutPct = (fadeOutSec / Math.max(0.01, durSec)) * 100;

  const label = voiceover.label ?? "Voiceover";
  const interactive = !!onPatch;
  // Playhead innerhalb dieses Segments? Bestimmt, ob der "Hier schneiden"
  // Eintrag im Kontextmenü aktiv ist.
  const playheadInside =
    playheadSec > fromSec + 0.05 && playheadSec < fromSec + durSec - 0.05;

  return (
    <div
      className={`music-bar voiceover-bar ${drag ? `dragging drag-${drag.kind}` : ""} ${interactive ? "interactive" : ""} ${selected ? "selected" : ""}`}
      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
      onMouseDown={(e) => {
        if (!interactive || e.button !== 0) return;
        e.stopPropagation();
        onSelect();
        onFocus();
        beginDrag({
          kind: "move",
          startClientX: e.clientX,
          startFromSec: fromSec,
        });
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
        onFocus();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect();
        setCtx({ x: e.clientX, y: e.clientY });
      }}
      title={`${label} · ${fromSec.toFixed(1)}s → ${(fromSec + durSec).toFixed(1)}s · Vol ${Math.round((voiceover.volume ?? 1) * 100)}%${trimStart > 0 ? ` · Schnitt ab ${trimStart.toFixed(2)}s` : ""}`}
    >
      {interactive && (
        <div
          className="music-bar-edge l"
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            onSelect();
            onFocus();
            beginDrag({
              kind: "resize-l",
              startClientX: e.clientX,
              startFromSec: fromSec,
              startDur: durSec,
              startTrim: trimStart,
            });
          }}
          title="Anfang ziehen — schneidet vorne ab (trimStart wandert mit)"
        />
      )}

      <span className="music-bar-bed">🎙️ {label}</span>
      <span className="music-bar-vol">Vol {Math.round((voiceover.volume ?? 1) * 100)}%</span>

      {(seg.src ?? voiceover.src) ? (
        <WaveformCanvas
          src={(seg.src ?? voiceover.src) as string}
          windowStartSec={trimStart}
          windowDurSec={durSec}
          colorTop="rgba(94, 234, 212, 0.95)"
          colorBottom="rgba(13, 148, 136, 0.9)"
          className="music-bar-waveform voiceover-waveform"
        />
      ) : null}

      {fadeInSec > 0 && (
        <div className="music-bar-fade in" style={{ width: `${fadeInPct}%` }} aria-hidden />
      )}
      {interactive && (
        <div
          className="music-bar-fade-handle in"
          style={{ left: `${fadeInPct}%` }}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            onSelect();
            onFocus();
            beginDrag({
              kind: "fade-in",
              startClientX: e.clientX,
              startFade: fadeInSec,
              durAtStart: durSec,
            });
          }}
          title={`Fade-In ${fadeInSec.toFixed(2)}s — ziehen zum Anpassen`}
        >
          <span className="music-bar-fade-knob">▶</span>
        </div>
      )}

      {fadeOutSec > 0 && (
        <div className="music-bar-fade out" style={{ width: `${fadeOutPct}%` }} aria-hidden />
      )}
      {interactive && (
        <div
          className="music-bar-fade-handle out"
          style={{ right: `${fadeOutPct}%` }}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            onSelect();
            onFocus();
            beginDrag({
              kind: "fade-out",
              startClientX: e.clientX,
              startFade: fadeOutSec,
              durAtStart: durSec,
            });
          }}
          title={`Fade-Out ${fadeOutSec.toFixed(2)}s — ziehen zum Anpassen`}
        >
          <span className="music-bar-fade-knob">◀</span>
        </div>
      )}

      {interactive && (
        <div
          className="music-bar-edge r"
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            e.stopPropagation();
            onSelect();
            onFocus();
            beginDrag({
              kind: "resize-r",
              startClientX: e.clientX,
              startFromSec: fromSec,
              startDur: durSec,
            });
          }}
          title="Länge ziehen — kürzt das Ende ab"
        />
      )}

      {drag && (
        <div className="clip-drag-tooltip">
          {drag.kind === "fade-in"
            ? `Fade-In ${fadeInSec.toFixed(2)}s`
            : drag.kind === "fade-out"
              ? `Fade-Out ${fadeOutSec.toFixed(2)}s`
              : drag.kind === "trim-start"
                ? `Schnitt @ ${trimStart.toFixed(2)}s`
                : drag.kind === "resize-l"
                  ? `${fromSec.toFixed(2)}s · Schnitt ${trimStart.toFixed(2)}s`
                  : `${fromSec.toFixed(2)}s → ${(fromSec + durSec).toFixed(2)}s`}
        </div>
      )}

      {ctx && (
        <div className="sfx-chip-ctx" style={{ left: ctx.x, top: ctx.y }} onMouseDown={(e) => e.stopPropagation()}>
          {onSplitAtPlayhead && (
            <button
              className="ctx-item"
              disabled={!playheadInside}
              onClick={() => {
                onSplitAtPlayhead();
                setCtx(null);
              }}
            >
              <span className="ctx-icon">✂</span> Bei Playhead schneiden
              <span className="ctx-shortcut">S</span>
            </button>
          )}
          <div className="ctx-sep" />
          <button
            className="ctx-item danger"
            onClick={() => {
              onRemove?.();
              setCtx(null);
            }}
          >
            <span className="ctx-icon">✕</span> Segment löschen
            <span className="ctx-shortcut">⌫</span>
          </button>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// EMBEDDED SFX CHIP — baked-in scene SFX (recipe); löschen → suppress index / mute scene.
// =============================================================================

const EmbeddedSfxChip: React.FC<{
  chip: EmbeddedSfxChipDesc;
  totalForBar: number;
  chipHeight: number;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}> = ({ chip, totalForBar, chipHeight, selected, onSelect, onRemove }) => {
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!ctx) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest(".sfx-chip-ctx")) return;
      setCtx(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCtx(null);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [ctx]);

  const durSec =
    chip.kind === "beat"
      ? getSfxLibEntry(chip.sound).defaultDurSec
      : chip.durSec;
  const label = chip.kind === "beat" ? getSfxLibEntry(chip.sound).label : "Szene";
  const emoji = chip.kind === "beat" ? getSfxLibEntry(chip.sound).emoji : "◆";

  const leftPct = (chip.fromSec / totalForBar) * 100;
  const widthPct = Math.max(0.35, (durSec / totalForBar) * 100);

  return (
    <div
      className={`sfx-chip sfx-chip--embedded ${selected ? "selected" : ""}`}
      style={{
        left: `${leftPct}%`,
        width: `${widthPct}%`,
        height: chipHeight,
        zIndex: 1,
      }}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        onSelect();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect();
        setCtx({ x: e.clientX, y: e.clientY });
      }}
      title={
        chip.kind === "beat"
          ? `Vorlagen-SFX · ${label}`
          : "Szene mit eingebettetem SFX (Detail-Karte folgt) · Löschen = Szene-SFX stumm"
      }
    >
      <span className="sfx-chip-emoji" aria-hidden>{emoji}</span>
      <span className="sfx-chip-label">{label}</span>
      {ctx && (
        <div
          className="sfx-chip-ctx"
          style={{ left: ctx.x, top: ctx.y }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            className="ctx-item danger"
            onClick={() => {
              onRemove();
              setCtx(null);
            }}
          >
            <span className="ctx-icon">✕</span> Entfernen / stumm
          </button>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// SFX CHIP — bar representation of an independent SFX event on the SFX lane.
// Same drag/resize/snap/ctx-menu UX as OverlayChip, but a single-lane track
// (no vertical lane changes) and a sound-specific look (emoji + label).
// =============================================================================

type SfxChipDrag =
  | { kind: "move";     startClientX: number; startFromSec: number }
  | { kind: "resize-l"; startClientX: number; startFromSec: number; startDur: number }
  | { kind: "resize-r"; startClientX: number; startFromSec: number; startDur: number };

const SFX_MIN_DUR = 0.1;

const SfxChip: React.FC<{
  sfxEvent: SfxEvent;
  totalForBar: number;
  chipHeight: number;
  pxPerSec: () => number;
  xToSec: (clientX: number) => number;
  snap: boolean;
  selected: boolean;
  onSelect: () => void;
  onPatch: (patch: { fromSec?: number; durSec?: number }) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onDragChange: (dragging: boolean) => void;
  buildSnapTargets: (excludeUid: string | null) => SnapTarget[];
}> = ({
  sfxEvent,
  totalForBar,
  chipHeight,
  pxPerSec,
  xToSec,
  snap,
  selected,
  onSelect,
  onPatch,
  onRemove,
  onDuplicate,
  onDragChange,
  buildSnapTargets,
}) => {
  const [drag, setDrag] = useState<SfxChipDrag | null>(null);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);

  // Stable propsRef so listeners attached on drag-start always see latest props.
  const propsRef = useRef({ sfxEvent, pxPerSec, xToSec, snap, onPatch, buildSnapTargets });
  propsRef.current = { sfxEvent, pxPerSec, xToSec, snap, onPatch, buildSnapTargets };

  const beginDrag = useCallback(
    (d: SfxChipDrag) => {
      setDrag(d);
      onDragChange(true);
      const onMove = (e: MouseEvent) => {
        const p = propsRef.current;
        const dxSec = p.xToSec(e.clientX) - p.xToSec(d.startClientX);
        const maybeSnap = (sec: number): number => {
          if (!p.snap) return sec;
          const targets = p.buildSnapTargets(p.sfxEvent.uid);
          return applySnap(sec, targets, p.pxPerSec()).sec;
        };
        if (d.kind === "move") {
          const next = Math.max(0, maybeSnap(d.startFromSec + dxSec));
          p.onPatch({ fromSec: next });
        } else if (d.kind === "resize-l") {
          const rawStart = d.startFromSec + dxSec;
          const snapStart = maybeSnap(rawStart);
          const end = d.startFromSec + d.startDur;
          const newStart = Math.max(0, Math.min(snapStart, end - SFX_MIN_DUR));
          p.onPatch({ fromSec: newStart, durSec: end - newStart });
        } else {
          const rawEnd = d.startFromSec + d.startDur + dxSec;
          const snapEnd = maybeSnap(rawEnd);
          const newDur = Math.max(SFX_MIN_DUR, snapEnd - d.startFromSec);
          p.onPatch({ durSec: newDur });
        }
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        setDrag(null);
        onDragChange(false);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [onDragChange],
  );

  useEffect(() => {
    if (!ctx) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest(".sfx-chip-ctx")) return;
      setCtx(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCtx(null);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [ctx]);

  const leftPct = (sfxEvent.fromSec / totalForBar) * 100;
  const widthPct = Math.max(0.4, (sfxEvent.durSec / totalForBar) * 100);
  const lib = getSfxLibEntry(sfxEvent.sound);
  const label = sfxEvent.label ?? lib.label;

  return (
    <div
      className={`sfx-chip ${selected ? "selected" : ""} ${drag ? "dragging" : ""}`}
      style={{ left: `${leftPct}%`, width: `${widthPct}%`, height: chipHeight, zIndex: 3 }}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        onSelect();
        beginDrag({
          kind: "move",
          startClientX: e.clientX,
          startFromSec: sfxEvent.fromSec,
        });
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect();
        setCtx({ x: e.clientX, y: e.clientY });
      }}
      title={`SFX: ${label} · ${sfxEvent.fromSec.toFixed(2)}s · ${sfxEvent.durSec.toFixed(2)}s`}
    >
      <div
        className="sfx-chip-edge l"
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          e.stopPropagation();
          onSelect();
          beginDrag({
            kind: "resize-l",
            startClientX: e.clientX,
            startFromSec: sfxEvent.fromSec,
            startDur: sfxEvent.durSec,
          });
        }}
      />
      <span className="sfx-chip-emoji" aria-hidden>{lib.emoji}</span>
      <span className="sfx-chip-label">{label}</span>
      <div
        className="sfx-chip-edge r"
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          e.stopPropagation();
          onSelect();
          beginDrag({
            kind: "resize-r",
            startClientX: e.clientX,
            startFromSec: sfxEvent.fromSec,
            startDur: sfxEvent.durSec,
          });
        }}
      />
      {drag && (
        <div className="clip-drag-tooltip">
          {sfxEvent.fromSec.toFixed(2)}s · {sfxEvent.durSec.toFixed(2)}s
        </div>
      )}
      {ctx && (
        <div className="sfx-chip-ctx" style={{ left: ctx.x, top: ctx.y }} onMouseDown={(e) => e.stopPropagation()}>
          <button
            className="ctx-item"
            onClick={() => {
              onDuplicate();
              setCtx(null);
            }}
          >
            <span className="ctx-icon">⎘</span> Duplizieren
            <span className="ctx-shortcut">⌘D</span>
          </button>
          <div className="ctx-sep" />
          <button
            className="ctx-item danger"
            onClick={() => {
              onRemove();
              setCtx(null);
            }}
          >
            <span className="ctx-icon">✕</span> Löschen
            <span className="ctx-shortcut">⌫</span>
          </button>
        </div>
      )}
    </div>
  );
};
